import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if push is supported
  const checkSupport = useCallback(() => {
    const supported = 
      'serviceWorker' in navigator && 
      'PushManager' in window && 
      'Notification' in window;
    
    console.log('[PUSH] Support check:', { 
      serviceWorker: 'serviceWorker' in navigator,
      PushManager: 'PushManager' in window,
      Notification: 'Notification' in window
    });
    
    return supported;
  }, []);

  // Check current subscription status
  const checkSubscription = useCallback(async () => {
    try {
      if (!('serviceWorker' in navigator)) {
        console.log('[PUSH] Service Worker not supported');
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      console.log('[PUSH] Current subscription:', subscription ? 'Active' : 'None');
      setIsSubscribed(!!subscription);
      return !!subscription;
    } catch (err) {
      console.error('[PUSH] Error checking subscription:', err);
      return false;
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    const init = async () => {
      const supported = checkSupport();
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
        await checkSubscription();
      }
    };
    
    init();
  }, [checkSupport, checkSubscription]);

  // Register service worker
  const ensureServiceWorker = async () => {
    try {
      // Check if already registered
      const existingReg = await navigator.serviceWorker.getRegistration('/');
      
      if (existingReg) {
        console.log('[PUSH] Using existing service worker');
        return existingReg;
      }
      
      // Register new service worker
      console.log('[PUSH] Registering new service worker...');
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      });
      
      // Wait for it to be ready
      await navigator.serviceWorker.ready;
      console.log('[PUSH] Service worker registered and ready');
      
      return registration;
    } catch (err) {
      console.error('[PUSH] Service worker registration failed:', err);
      throw err;
    }
  };

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      return { success: false, error: 'Push notifications tidak disokong pada peranti ini' };
    }

    setLoading(true);
    setError(null);
    
    try {
      // Step 1: Request permission
      console.log('[PUSH] Requesting permission...');
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        console.log('[PUSH] Permission denied');
        return { success: false, error: 'Kebenaran notifikasi ditolak' };
      }
      console.log('[PUSH] Permission granted');

      // Step 2: Ensure service worker is registered
      await ensureServiceWorker();
      const registration = await navigator.serviceWorker.ready;
      console.log('[PUSH] Service worker ready');

      // Step 3: Get VAPID public key from server
      console.log('[PUSH] Fetching VAPID key...');
      const { data: vapidData } = await axiosInstance.get('/push/vapid-public-key');
      
      if (!vapidData?.publicKey) {
        throw new Error('VAPID key tidak dikonfigurasi di server');
      }
      console.log('[PUSH] VAPID key received');

      // Step 4: Check for existing subscription
      let subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        console.log('[PUSH] Found existing subscription, unsubscribing first...');
        await subscription.unsubscribe();
      }

      // Step 5: Create new subscription
      console.log('[PUSH] Creating new subscription...');
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidData.publicKey)
      });
      
      console.log('[PUSH] Subscription created:', subscription.endpoint);

      // Step 6: Send subscription to server
      const subscriptionJson = subscription.toJSON();
      console.log('[PUSH] Sending subscription to server...');
      
      await axiosInstance.post('/push/subscribe', {
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys
      });

      console.log('[PUSH] Subscription saved to server');
      setIsSubscribed(true);
      
      return { success: true };
    } catch (err) {
      console.error('[PUSH] Subscribe error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Notify server
        const subscriptionJson = subscription.toJSON();
        try {
          await axiosInstance.post('/push/unsubscribe', {
            endpoint: subscriptionJson.endpoint,
            keys: subscriptionJson.keys
          });
        } catch (e) {
          console.warn('[PUSH] Server unsubscribe failed, continuing locally');
        }
        
        // Unsubscribe locally
        await subscription.unsubscribe();
        console.log('[PUSH] Unsubscribed successfully');
      }
      
      setIsSubscribed(false);
      return { success: true };
    } catch (err) {
      console.error('[PUSH] Unsubscribe error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    try {
      console.log('[PUSH] Sending test notification...');
      const { data } = await axiosInstance.post('/push/test');
      console.log('[PUSH] Test result:', data);
      return data;
    } catch (err) {
      console.error('[PUSH] Test notification error:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Reset subscription (unsubscribe and resubscribe)
  const resetSubscription = useCallback(async () => {
    setLoading(true);
    try {
      // First unsubscribe
      await unsubscribe();
      
      // Wait a moment
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Then subscribe again
      const result = await subscribe();
      return result;
    } catch (err) {
      console.error('[PUSH] Reset error:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [subscribe, unsubscribe]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification,
    checkSubscription,
    resetSubscription
  };
}
